// Unified Tasks page (#681/#721 Phase 7c, refined into a 3-way split):
// Tab 1 "Team Tasks" is the instant/adhoc bot list (the old standalone
// Events page, unchanged); Tab 2 "Checklists" is the SOP-template bots that
// aren't adhoc-enabled, using the same stats table but tagged by
// daily/weekly/monthly/long_running lifecycle; Tab 3 "Others" lists FORM
// bots (attendance, process data, and any other non-checklist data-capture
// workflow) with today's entry count.

import { useMemo } from 'react';
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Badge,
  Highlight,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { DataTable } from '@/design-system/components/DataTable';
import { EventsPanel } from '@/modules/adhoc';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useChatbots, useDataGroupsForBots } from '@/lib/sdk/chatbot/queries';
import type { Chatbot } from '@/lib/sdk/chatbot/types';

interface FormBotRow {
  bot: Chatbot;
  entriesToday: number;
}

const tabDescriptionHighlight = {
  px: '1',
  py: '0.5',
  borderRadius: 'sm',
  bg: 'brand.50',
  color: 'brand.600',
  fontWeight: 'semibold',
};

// "Others" tab (#721): FORM-template bots — the only template besides SOP,
// covering attendance, process data, and any other data-capture workflow
// that isn't a checklist. Entry count for the current date per bot, drilling
// into the existing per-bot TasksPage (workflows/:botId/data) unchanged.
function OthersTab() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? '';
  const navigate = useNavigate();
  const today = dayjs().format('YYYY-MM-DD');
  // GetDataForDateRange filters `task_date >= from_date AND task_date < to_date`
  // (exclusive upper bound) — end_date must be tomorrow to capture today's
  // rows at all, same as TasksPage.tsx's own default range does.
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

  const { data: chatbotsData, isLoading: botsLoading } = useChatbots({ orgId, limit: 100, active_only: true });
  const formBots = useMemo(
    () => (chatbotsData?.items ?? []).filter((bot) => bot.template === 'form'),
    [chatbotsData]
  );

  const entryResults = useDataGroupsForBots(
    formBots.map((bot) => bot.id),
    { start_date: today, end_date: tomorrow }
  );

  const rows = useMemo<FormBotRow[]>(
    () => formBots.map((bot, index) => ({
      bot,
      entriesToday: entryResults[index]?.data?.total ?? 0,
    })),
    [formBots, entryResults]
  );

  const totalEntriesToday = rows.reduce((sum, r) => sum + r.entriesToday, 0);

  const columns = useMemo<ColumnDef<FormBotRow>[]>(
    () => [
      {
        id: 'title',
        header: 'Workflow',
        cell: ({ row }) => (
          <Text fontWeight="bold" color="brand.500" cursor="pointer" _hover={{ textDecoration: 'underline' }}
            data-testid={`tasks-others-row-title-${row.original.bot.id}`}
            onClick={() => navigate(`/dashboard/${orgId}/workflows/${row.original.bot.id}/data`)}>
            {row.original.bot.title}
          </Text>
        ),
      },
      {
        id: 'entriesToday',
        header: 'Entries Today',
        cell: ({ row }) => (
          row.original.entriesToday > 0
            ? <Badge colorScheme="green">{row.original.entriesToday}</Badge>
            : <Text color="text-muted">0</Text>
        ),
        size: 140,
      },
    ],
    [navigate, orgId]
  );

  return (
    <>
      {formBots.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4} mb={6}>
          <Card data-testid="tasks-others-stat-count">
            <CardBody py={4} px={5}>
              <Stat>
                <StatLabel fontSize="xs" color="text-secondary">Workflows</StatLabel>
                <StatNumber fontSize="2xl" color="brand.500">{formBots.length}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card data-testid="tasks-others-stat-entries-today">
            <CardBody py={4} px={5}>
              <Stat>
                <StatLabel fontSize="xs" color="text-secondary">Entries Today</StatLabel>
                <StatNumber fontSize="2xl" color={totalEntriesToday > 0 ? 'green.500' : 'text-primary'}>
                  {totalEntriesToday}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      <DataTable
        data={rows} columns={columns} isLoading={botsLoading}
        emptyMessage="No workflows found."
        enableSorting enableFiltering enablePagination pageSize={20}
      />
    </>
  );
}

export function TasksHomePage() {
  return (
    <PageContainer title="Tasks" description="Task activity across your workflows">
      {/* isLazy: without it Chakra mounts every TabPanel upfront, so all
          three tabs would fetch their bot lists on page load regardless of
          which one is visible — wasted requests, and duplicate
          data-testids (one per mounted EventsPanel) for anything that
          inspects the DOM. */}
      <Tabs isLazy>
        <TabList>
          <Tab data-testid="tasks-tab-team">Team Tasks</Tab>
          <Tab data-testid="tasks-tab-checklists">Checklists</Tab>
          <Tab data-testid="tasks-tab-others">Others</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Text color="text-primary" fontSize="md" fontWeight="medium" mb={4} data-testid="tasks-tab-description-team">
              <Highlight query={['Instant Tasks', 'create a new group']} styles={tabDescriptionHighlight}>
                {'One-off tasks assigned to individual team members, with live progress at a glance. Most tasks are linked to the default "Instant Tasks" group — create a new group to organize specific tasks for a particular team, e.g. an "Events" group for tasks tied to a specific event.'}
              </Highlight>
            </Text>
            <EventsPanel />
          </TabPanel>
          <TabPanel px={0}>
            <Text color="text-primary" fontSize="md" fontWeight="medium" mb={4} data-testid="tasks-tab-description-checklists">
              <Highlight query={['daily, weekly, or monthly']} styles={tabDescriptionHighlight}>
                {'Recurring checklists your team works through step by step, each tagged by how often it repeats — daily, weekly, or monthly.'}
              </Highlight>
            </Text>
            <EventsPanel includeSop variant="checklist" />
          </TabPanel>
          <TabPanel px={0}>
            <Text color="text-primary" fontSize="md" fontWeight="medium" mb={4} data-testid="tasks-tab-description-others">
              <Highlight query={['attendance, KYC, route cards, and guest check-ins']} styles={tabDescriptionHighlight}>
                {'Structured forms your team fills out for processes like attendance, KYC, route cards, and guest check-ins — see how many entries came in today.'}
              </Highlight>
            </Text>
            <OthersTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </PageContainer>
  );
}
